/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircleIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { BookingDetails } from '../types';
import { portfolioItems } from '@shared/data/portfolioData';
import Spinner from './Spinner';
import { DiscountService } from '../services/discountService';
import { useLocalizedPrice } from '../utils/priceUtils';

interface BookingConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  bookingDetails: BookingDetails | null;
}

const BookingConfirmationModal: React.FC<BookingConfirmationModalProps> = ({ isOpen, onClose, onConfirm, isLoading, bookingDetails }) => {
  const { t } = useTranslation();
  const { formatPrice } = useLocalizedPrice();

  if (!bookingDetails) return null;

  const getServiceNames = (ids: string[]) => {
    return ids.map(id => {
      const service = portfolioItems.find(item => item.id === id);
      const baseTier = service?.pricingTiers.find(t => t.basePriceEUR) || service?.pricingTiers[0];
      const basePrice = baseTier?.basePriceEUR || 0;
      const discount = DiscountService.calculateDiscount(basePrice, id);
      const priceLabel = basePrice ? ` (${formatPrice(discount.discountedPrice)})` : '';
      return service ? `${t(service.titleKey)}${priceLabel}` : id;
    }).join(', ');
  };

  const formattedDate = new Date(`${bookingDetails.date}T${bookingDetails.time}`).toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-800"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {t('bookingConfirmation_title')}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title={t('common_close')} aria-label={t('common_close')}>
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar max-h-[60vh]">
              <p className="text-gray-600 dark:text-gray-300 mb-2">{t('bookingConfirmation_subtitle')}</p>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl space-y-3">
                  <div>
                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1">{t('bookingConfirmation_services')}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{getServiceNames(bookingDetails.serviceIds)}</p>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700/50 pt-3">
                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1">{t('bookingConfirmation_dateTime')}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{formattedDate}</p>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl space-y-3">
                  <div>
                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1">{t('bookingConfirmation_yourDetails')}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium"><span className="font-bold">Name:</span> {bookingDetails.name}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium"><span className="font-bold">Email:</span> {bookingDetails.email}</p>
                    {bookingDetails.phone && <p className="text-sm text-gray-700 dark:text-gray-300 font-medium"><span className="font-bold">Phone:</span> {bookingDetails.phone}</p>}
                  </div>
                </div>
                {bookingDetails.notes && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl">
                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1">{t('bookingConfirmation_notes')}</p>
                    <p className="text-sm italic text-gray-600 dark:text-gray-400 border-l-4 border-indigo-500 pl-3 py-1 whitespace-pre-wrap">{bookingDetails.notes}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-800/50 flex flex-col sm:flex-row gap-3">
              <button onClick={onClose} disabled={isLoading} className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors active:scale-95 disabled:opacity-50">
                {t('bookingConfirmation_editButton')}
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="flex-1 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? <Spinner className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />}
                {t('bookingConfirmation_confirmButton')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingConfirmationModal;
